import React, {Fragment, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Pagination,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {
    OperationApiGet,
    useDeleteOperationByIdMutation,
    useGetOperationQuery,
    usePutOperationByIdMutation,
} from "./operation_api";
import Cancel from "@mui/icons-material/Cancel";
import Delete from "@mui/icons-material/Delete";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Edit from "@mui/icons-material/Edit";
import Upload from "@mui/icons-material/Upload";
import {SearchBox} from "../../components/SearchBox";
import sortComparator from "../../libs/sortComparator";
import filterFunction from "../../libs/filterFunction";
import pager from "../../libs/pager";
import {Route, Routes, useHref} from "react-router-dom";
import {OperationCreate} from "./OperationCreate";
import {OperationEdit} from "./OperationEdit";
import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";
import usePaginator from "../../hooks/usePaginator";
import {ListCardDataCol, ListGrid} from "../../components/styled";
import placeholder from "/assets/imgs/logo.png";
import useDownloadImage from "../storage/useDownloadImage";
import ImageUploadDialog from "../storage/ImageUploadDialog";

export type OperationItemProps = {
    onDelete: (id: number) => void,
    item: OperationApiGet,
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

const OperationItem = (props: OperationItemProps) => {
    const {item, selectable, onSelect} = props;

    const [put, {
        isLoading: isUpdating,
        error,
        isError,
    }] = usePutOperationByIdMutation();

    const [uploading, setUploading] = useState(false)

    const onUpload = (path: string) => {
        put({body: {image_url: path}, id: item.id})
        setUploading(false)
    }
    const avatarUrl = useDownloadImage("machines", item.image_url)


    const path = useHref(`./${item.id}/edit`);

    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card sx={{
        display: "flex"
    }} onClick={() => {
        selectable && onSelect && onSelect(item.id);
    }}>
        <SnackError error={error}/>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={avatarUrl || placeholder}
            alt={item.Name}
        />
        <ListCardDataCol> <CardContent>

            <Typography variant={"subtitle1"}> {item.Code} {item.Active === false && "Neaktiven"}</Typography>
            <Typography variant={"h6"}>{item.Name}</Typography>

        </CardContent>
            {!selectable && <CardActions>

                <Button onClick={() => setUploading(true)}
                        title={"Naloži sliko"}
                ><Upload fontSize={"small"}/></Button>

                <Button
                    title={item.Active ? "Onemogoči" : "Omogoči"}
                    sx={{
                        color: item.Active ? undefined : "error.main"
                    }} onClick={() => {
                    put({body: {Active: !item.Active}, id: item.id})
                }}>{!item.Active ? <Cancel/> : <DoNotDisturb/>}</Button>

                <Button href={path}
                        title={"Uredi operacijo"}
                ><Edit fontSize={"small"}/></Button>
                <Button title="Briši operacijo"
                        onClick={() => {
                            props.onDelete(item.id);
                        }}
                ><Delete fontSize={"small"}/> </Button>
            </CardActions>}

            <ImageUploadDialog
                uploading={uploading}
                onUpload={onUpload}
                bucket={"machines"}
                onClose={() => setUploading(false)}
            />
        </ListCardDataCol>
    </Card>
}


export type OperationListProps = {
    list: OperationApiGet[],
    selectable?: boolean;
    onSelect?: (id: number, machineId?: number|null) => void;
}

export const OperationList = (props: OperationListProps) => {
    const {list, onSelect, selectable} = props;

    const [deleteOperation, {
        error: deleteOperationError
    }] = useDeleteOperationByIdMutation();

    const handleDelete = (id: number) => {
        deleteOperation({id});
    }

    return <ListGrid>
        {deleteOperationError && <SnackError error={deleteOperationError}/>}
        {list.map(param => <OperationItem key={param.id} item={param}
                                          onSelect={onSelect}
                                          selectable={selectable}
                                          onDelete={handleDelete}/>)}
    </ListGrid>
}

type OpControllerProps = {
    pageSize?: number;
    selectable?: boolean;
    onSelect?: (id: number, machineID?: number|null ) => void;
}

const sortFields = {
    Code: "Koda",
    Name: "Naziv",
    Active: "Aktiven",
}

export const OpListController = ({pageSize, selectable, onSelect}: OpControllerProps) => {

    const {
        filter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("operations", sortFields,)
    const path = useHref("./create");
    const {data, error, isFetching} = useGetOperationQuery({});

    if (error) {
        return <SnackError error={error}/>;
    }
    if (isFetching || !data) {
        return <CircularProgress/>
    }


    const list: Array<OperationApiGet> = data && data.filter(filterFunction(filter, Object.keys(sortFields)))
        .sort(sortComparator(sortDirection, sortField)) || [];
    const [pages, currentPage] = pager(list, page, pageSize);

    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>
            {!selectable && <Button href={path}>Ustvari operacijo</Button>}
            <Box sx={{display: "flex", ml: "auto"}}>
                <SearchBox value={filter} onChange={setFilter}/>
                <SortBox sortDirection={sortDirection} sortField={sortField}
                         onChangeDirection={setSortDirection}
                         onSelectField={setSortField} fields={sortFields}
                />
            </Box>
        </Toolbar>

        <OperationList list={currentPage} selectable={selectable} onSelect={onSelect}/>
        <Pagination page={page} onChange={(e, page) => setPage(page)} count={pages}/>
    </Fragment>
}

export type OperationProps = {
    pageSize?: number
}

export default ({pageSize = 15}: OperationProps) => {


    return <Fragment>
        <Title title={"Upravljanje operacij"}/>
        <Routes>
            <Route path={"/create"} element={<OperationCreate/>}/>
            <Route path={"/:id/edit"} element={<OperationEdit/>}/>
            <Route path={"/"} element={<OpListController pageSize={pageSize}/>}/>
        </Routes>
    </Fragment>
}
